import React from 'react';
import PropTypes from 'prop-types';
import round from 'lodash/round';

import { buildImgixUrl, candidateWidths, pickFallbackWidth } from 'Helpers/Image/image-urls';

// What the worker replaces with the slot's absolute image URL (see src/skeletons/README.md)
export const SLOT_VALUE = '{value}';

// For `media`: bootstrap's media-breakpoint-up(md) and media-breakpoint-down(sm)
export const FROM_MD = '(min-width: 768px)';
export const BELOW_MD = '(max-width: 767.98px)';

// A transparent pixel, for media where the image is hidden
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * An image slot for skeletons: an <img> with no src, which the worker fills per request (e.g. the product cover).
 * The srcset and src are templates around the image URL, built with the same imgix helpers as FluidImg, so the
 * skeleton requests the same URLs the page will. Components with their own srcset can pass their builder as
 * `sources` instead. Unfilled, it renders nothing and its container shows grey.
 * An image only shown at some widths (by its container's CSS) needs `media`: a hidden eager image is still fetched,
 * at high priority, so it's in a <picture> whose blank source the browser picks elsewhere.
 */
export default function SkeletonImg({
  slot,
  sizes,
  aspectRatio,
  extraWidths,
  maxFallbackWidth,
  sources,
  media,
  className,
}) {
  const fluidSources = (url) => {
    const widths = [...new Set([...candidateWidths(), ...extraWidths])].sort((a, b) => b - a);
    const toUrl = width => buildImgixUrl(url, {
      width,
      ...(aspectRatio ? { fit: 'crop', height: round(width / aspectRatio) } : { fit: 'max' }),
    });
    return {
      srcSet: widths.map(width => `${toUrl(width)} ${width}w`).join(', '),
      src: toUrl(pickFallbackWidth(widths, maxFallbackWidth)),
    };
  };
  const { srcSet, src } = (sources || fluidSources)(SLOT_VALUE);

  const img = (
    <img
      data-skeleton-slot={slot}
      data-slot-srcset={srcSet}
      data-slot-src={src}
      sizes={sizes}
      alt=""
      loading="eager"
      fetchpriority="high"
      className={`img-fluid img-full ${className}`}
    />
  );

  if (!media) {
    return img;
  }

  return (
    <picture>
      <source media={`not all and ${media}`} srcSet={BLANK} />
      {img}
    </picture>
  );
}

SkeletonImg.propTypes = {
  slot: PropTypes.string.isRequired,
  sizes: PropTypes.string.isRequired,
  aspectRatio: PropTypes.number,
  extraWidths: PropTypes.arrayOf(PropTypes.number),
  maxFallbackWidth: PropTypes.number,
  // url => ({ srcSet, src }), for images built by their own helper rather than like FluidImg
  sources: PropTypes.func,
  // The media query the image is shown at, e.g. '(min-width: 768px)', if its container hides it at others
  media: PropTypes.string,
  className: PropTypes.string,
};

SkeletonImg.defaultProps = {
  aspectRatio: 0,
  extraWidths: [],
  maxFallbackWidth: 400,
  sources: null,
  media: null,
  className: '',
};
